import React from 'react';

// Types
import { User } from 'Shared/constants/types';
import { BAFactorShapeType } from 'Shared/components/BehavioralPatternGraph/FactorShape.service';

// Hooks
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useBaProfileDrawer } from 'Shared/utils/hooks/use-ba-profile-drawer';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import BaProfileDisplayHeader from 'Shared/components/UserBaProfileSnapshot/_partials/BaProfileDisplayHeader';
import { WpIcon } from 'Wpds/components/icon';
import RelationshipGuideInsights from './RelationshipGuideInsights';
import CombinedRelationship from './CombinedRelationship/CombinedRelationship';
import UserBaProfileSnapshot from 'Shared/components/UserBaProfileSnapshot/UserBaProfileSnapshot';

// Styles
import './WorkspaceRelationship.scss';
import { useFlags } from 'launchdarkly-react-client-sdk';
import FullRelationshipGuideInsights from './FullRelationshipGuideInsights';

type Props = {
  user: User;
  otherUser: User;
};

const WorkspaceRelationship = ({ user, otherUser }: Props): JSX.Element => {
  const { slug: currentUserSlug } = useCurrentUser();
  const { isXtraSmall } = useResponsiveScreenWidth({ isAtMost: true });
  // Adjusted XL breakpoint to match scss breakpoint for this component
  const { isXtraLarge: isAdjustedXL } = useResponsiveScreenWidth({ isAtLeast: true, breakpoints: { xl: 1845 } });
  const { baProfile: userBaProfile } = useBaProfile({
    user,
  });
  const { baProfile: otherUserInOneOnOneBaProfile } = useBaProfile({ user: otherUser });
  const { enableRelationshipGuideInsights } = useFlags();

  const { handleOpenBaDrawer: handleOpenBaDrawerForUser } = useBaProfileDrawer({
    user: user,
    shape: BAFactorShapeType.Square,
  });
  const { handleOpenBaDrawer: handleOpenBaDrawerForOtherUser } = useBaProfileDrawer({
    user: otherUser,
    shape: BAFactorShapeType.Circle,
  });

  const baseClassName = 'WorkspaceRelationship';

  const BaProfileMarkup = ({ className, user }: { className: string; user: User }) => {
    const isCurrentUser = user.slug === currentUserSlug;
    return (
      <>
        {isXtraSmall ? (
          <WpContainer
            className={`${baseClassName}__ba-profile ${className}`}
            onClick={isCurrentUser ? handleOpenBaDrawerForUser : handleOpenBaDrawerForOtherUser}
          >
            <WpContainerBody padding="05" className={`${baseClassName}__mobile-ba-header`}>
              <BaProfileDisplayHeader user={user} />
              <WpIcon name="angle-right" size="lg" />
            </WpContainerBody>
          </WpContainer>
        ) : (
          <WpContainer className={`${baseClassName}__ba-profile ${className}`}>
            <WpContainerBody>
              <UserBaProfileSnapshot
                inContainer={false}
                user={user}
                useDivider={false}
                showStrengthsAndCaution={false}
                shapeType={isCurrentUser ? BAFactorShapeType.Square : BAFactorShapeType.Circle}
              />
            </WpContainerBody>
          </WpContainer>
        )}
      </>
    );
  };

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__content`}>
        <BaProfileMarkup user={otherUser} className={`${baseClassName}__ba-profile--left`} />
        <div className={`${baseClassName}__combined-ba-pattern`}>
          <CombinedRelationship useFullHeight={isAdjustedXL ? true : false} />
        </div>
        <BaProfileMarkup user={user} className={`${baseClassName}__ba-profile--right`} />
      </div>
      <div className={`${baseClassName}__full-guide`}>
        {enableRelationshipGuideInsights ? (
          <RelationshipGuideInsights
            selfSurveyUserId={userBaProfile.assessmentUserId}
            colleagueSurveyUserId={otherUserInOneOnOneBaProfile.assessmentUserId}
          />
        ) : (
          <FullRelationshipGuideInsights
            selfSurveyUserId={userBaProfile.assessmentUserId}
            colleagueSurveyUserId={otherUserInOneOnOneBaProfile.assessmentUserId}
          />
        )}
      </div>
    </div>
  );
};

export default WorkspaceRelationship;
